// SPDX-FileCopyrightText: 2026 Ebiose AI
// SPDX-License-Identifier: LicenseRef-Ebiose-Proprietary
// All rights reserved. Confidential — unauthorized distribution prohibited.

import Chart from "react-apexcharts"
import type { ApexOptions } from "apexcharts"
import type { DistributionSummary } from "./summarize"
import type { ReferenceSeries } from "../referenceSet/derive"
import { referenceFitnessAnnotations } from "../referenceSet/annotations"

export interface FitnessDistributionChartProps {
  summaries: ReadonlyArray<DistributionSummary>
  /**
   * The reference overlay — dashed accent level line at the reference
   * MAX (mockup 1b); the min–max spread and the two derived gain deltas
   * stay disclosed in the accessible caption. `null`/absent → no
   * overlay, the box-plot renders exactly today's dashboard.
   */
  referenceSeries?: ReferenceSeries | null
}

const signed = (n: number): string => `${n >= 0 ? "+" : ""}${n.toFixed(2)}`

const APEX_OPTIONS: ApexOptions = {
  chart: {
    type: "boxPlot",
    height: 400,
    background: "transparent",
    toolbar: { show: false },
    animations: { enabled: false },
    zoom: { enabled: false },
  },
  theme: { mode: "dark" },
  // Explicitly off: ApexCharts v5 otherwise reserves per-side data-label
  // width in its layout pass, shrinking the grid (and the reference
  // level line drawn from it) to ~60% of the card.
  dataLabels: { enabled: false },
  xaxis: {
    type: "category",
    // Keep a true category axis: without it ApexCharts silently converts
    // the axis to numeric for bar-family charts and pads the grid by a
    // half column-width per side — truncating the reference level line
    // (drawn from gridWidth) to ~60% of the plot.
    tickPlacement: "between",
    labels: { style: { colors: "#5f8c84" } },
    title: {
      text: "Iterations",
      style: { color: "#5f8c84", fontSize: "16px" },
    },
  },
  yaxis: {
    min: 0,
    title: {
      text: "Fitness Score",
      style: { color: "#5f8c84", fontSize: "16px" },
    },
    labels: {
      style: { colors: "#5f8c84" },
      formatter: (value: number) => value.toFixed(1),
    },
  },
  // Mockup 1b box styling: monochrome mint fill, whiskers/border/median
  // all drawn in the same mint stroke (ApexCharts has a single stroke
  // color per boxPlot series — the solid median-on-top reads through the
  // translucent fill).
  stroke: { colors: ["#45f0c8"], width: 1.5 },
  plotOptions: {
    boxPlot: {
      colors: {
        upper: "rgba(69,240,200,0.2)",
        lower: "rgba(69,240,200,0.2)",
      },
    },
  },
  grid: { borderColor: "#11393f" },
  tooltip: {
    theme: "dark",
    y: { formatter: (value: number) => value.toFixed(2) },
  },
  // No legend (mockup 1b) — the old hidden-name right legend also made
  // ApexCharts reserve ~1/3 of the width in its layout pass, truncating
  // the reference level line and offsetting the grid.
  legend: { show: false },
}

export default function FitnessDistributionChart({
  summaries,
  referenceSeries = null,
}: FitnessDistributionChartProps) {
  const summariesJson = JSON.stringify(summaries)

  // `#N` labels (mockup 1b). Also load-bearing: a numeric-parseable
  // category ("1") flips ApexCharts to a numeric x-axis, which reserves
  // a half column-width of grid padding per side for bar-family charts —
  // shrinking the grid the reference level line is drawn from.
  const boxPlotData = summaries.map((s) => ({
    x: `#${s.iter_index + 1}`,
    y: [s.min, s.q1, s.median, s.q3, s.max],
  }))

  const series = [
    {
      name: "Fitness Scores",
      type: "boxPlot" as const,
      data: boxPlotData,
    },
  ]

  const options =
    referenceSeries === null
      ? APEX_OPTIONS
      : {
          ...APEX_OPTIONS,
          annotations: referenceFitnessAnnotations(referenceSeries),
        }

  return (
    <div className="w-full">
      <div
        data-testid="fitness-distribution-chart"
        data-distribution-summaries={summariesJson}
        {...(referenceSeries !== null
          ? { "data-reference-series": JSON.stringify(referenceSeries) }
          : {})}
        role="figure"
        aria-label="Fitness distribution per iteration"
        className="w-full"
      >
        <Chart options={options} series={series} type="boxPlot" height={400} />
      </div>
      {referenceSeries !== null && (
        <figcaption
          data-testid="reference-overlay-caption"
          className="sr-only"
        >
          {referenceSeries.label}
          {referenceSeries.hasBand
            ? ` (min ${referenceSeries.min.toFixed(2)} – max ${referenceSeries.max.toFixed(2)})`
            : ""}
          {referenceSeries.architectsGain !== null && (
            <span>
              {" · architects' gain "}
              {signed(referenceSeries.architectsGain)}
            </span>
          )}
          {referenceSeries.evolutionGain !== null && (
            <span>
              {" · evolution's gain "}
              {signed(referenceSeries.evolutionGain)}
            </span>
          )}
        </figcaption>
      )}
    </div>
  )
}
